/**
 * Build 5: how each shared register is cut into rows (see registerRows.ts).
 *
 * A row is the thing two people might each change: one docking unit, one
 * piece of equipment, one inspection, one contractor, one contract. Row ids
 * carry what kind of row they are ("item/…", "change/…") where a register
 * holds more than one kind, and never change once written.
 */
import { t } from "../i18n";
import type { RegisterSpec } from "./registerRows";
import { VDGS_TOPIC, type VdgsRegister, type VdgsUnit } from "./vdgs";
import type { FleetItem, FleetRegister } from "./fleet";
import type { FleetChange } from "./fleetEdit";
import {
  INSPECTION_TOPIC,
  type Contractor,
  type InspectionRecord,
  type InspectionStore,
  type RepairRecord,
  type TradeId,
} from "./assetInspections";
import { mergeRegisters, type ContractRegister, type ContractVersion } from "./contracts";

type Rows = readonly { id: string; data: Record<string, unknown> }[];
const asRow = (v: object) => v as unknown as Record<string, unknown>;
const kindOf = (id: string) => id.slice(0, id.indexOf("/"));
const rest = (id: string) => id.slice(id.indexOf("/") + 1);

/* ── docking units: one row per stand ─────────────────────────────────── */

/**
 * A unit's dockings are not kept with it. They are what the unit logs, not
 * what anybody enters, and keeping the sample's in the record froze them on
 * the day of the first save (audit: "dockings freeze a day after the first
 * save"). The screen attaches them from the docking log — the sample's on the
 * demo, none on the platform until a unit feed exists.
 */
export const VDGS_SPEC: RegisterSpec<VdgsRegister> = {
  key: "vdgs",
  topic: VDGS_TOPIC,
  toRows: (reg) => {
    const m = new Map<string, Record<string, unknown>>();
    for (const u of reg.units) {
      const { dockings: _logged, ...kept } = u;
      void _logged;
      m.set(u.stand, asRow(kept));
    }
    return m;
  },
  fromRows: (rows: Rows) => ({
    units: rows.map((r) => ({ aircraftTypes: [], ...(r.data as Partial<VdgsUnit>), stand: r.id, dockings: [] }) as VdgsUnit),
  }),
  describe: (id) => t("the docking unit on stand {stand}", { stand: id }),
};

/* ── equipment: one row per item, one per change in its history ───────── */

export const EQUIPMENT_TOPIC = "estate.equipment";

export const EQUIPMENT_SPEC: RegisterSpec<FleetRegister> = {
  key: "equipment",
  topic: EQUIPMENT_TOPIC,
  toRows: (reg) => {
    const m = new Map<string, Record<string, unknown>>();
    for (const i of reg.items) m.set(`item/${i.id}`, asRow(i));
    for (const c of reg.changes ?? []) m.set(`change/${c.id}`, asRow(c));
    return m;
  },
  fromRows: (rows: Rows) => {
    const items: FleetItem[] = [];
    const changes: FleetChange[] = [];
    for (const r of rows) {
      const k = kindOf(r.id);
      if (k === "item") items.push({ ...(r.data as unknown as FleetItem), id: rest(r.id) });
      else if (k === "change") changes.push(r.data as unknown as FleetChange);
    }
    /* the history is oldest first, as fleetEdit.ts appends it */
    changes.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
    return { items, changes };
  },
  describe: (id, data) =>
    kindOf(id) === "item"
      ? t("equipment item {tag}", { tag: String((data as { tag?: string } | undefined)?.tag ?? rest(id)) })
      : t("an entry in the equipment history"),
};

/* ── facility inspections: one row per inspection, contractor, past repair and work-order link ── */

/**
 * An inspection is written once, when it is saved. The work order raised for
 * one of its findings is a row of its own (`link/<inspection>|<item>`), so
 * raising work never rewrites the inspection — two people raising orders for
 * two findings of one inspection never meet, and a link is never lost to a
 * colleague's save. Whether the work is done is read from the work-order
 * register each time (`workDone`) and never kept here.
 */
export const FACILITY_SPEC: RegisterSpec<InspectionStore> = {
  key: "facility-inspections",
  topic: INSPECTION_TOPIC,
  toRows: (store) => {
    const m = new Map<string, Record<string, unknown>>();
    for (const [trade, c] of Object.entries(store.contractors ?? {})) if (c) m.set(`contractor/${trade}`, asRow(c));
    for (const r of store.inspections ?? []) {
      const faults = r.faults.map((f) => {
        const { workOrderId, workOrderNumber, workDone: _done, ...kept } = f;
        void _done;
        if (workOrderId)
          m.set(`link/${r.id}|${f.itemId}`, { inspectionId: r.id, itemId: f.itemId, workOrderId, workOrderNumber });
        return kept;
      });
      m.set(`inspection/${r.id}`, asRow({ ...r, faults }));
    }
    for (const h of store.history ?? []) m.set(`repair/${h.id}`, asRow(h));
    return m;
  },
  fromRows: (rows: Rows) => {
    const contractors: Partial<Record<TradeId, Contractor>> = {};
    const inspections: InspectionRecord[] = [];
    const history: RepairRecord[] = [];
    const links = new Map<string, { workOrderId?: string; workOrderNumber?: string }>();
    for (const r of rows) {
      const k = kindOf(r.id);
      if (k === "contractor") contractors[rest(r.id) as TradeId] = r.data as unknown as Contractor;
      else if (k === "inspection") inspections.push(r.data as unknown as InspectionRecord);
      else if (k === "repair") history.push(r.data as unknown as RepairRecord);
      else if (k === "link") links.set(rest(r.id), r.data as { workOrderId?: string; workOrderNumber?: string });
    }
    const linked = links.size
      ? inspections.map((rec) => ({
          ...rec,
          faults: rec.faults.map((f) => {
            const l = links.get(`${rec.id}|${f.itemId}`);
            return l?.workOrderId ? { ...f, workOrderId: l.workOrderId, workOrderNumber: l.workOrderNumber } : f;
          }),
        }))
      : inspections;
    return { version: 1, contractors, inspections: linked, history };
  },
  describe: (id, data) => {
    const k = kindOf(id);
    if (k === "contractor") return t("the contractor for {trade}", { trade: rest(id) });
    if (k === "inspection")
      return t("inspection {id} of {facility}", {
        id: rest(id),
        facility: String((data as { facilityId?: string } | undefined)?.facilityId ?? ""),
      });
    if (k === "link") return t("the work order for finding {item}", { item: rest(id).split("|")[1] ?? rest(id) });
    return t("a past repair");
  },
};

/* ── contracts: one row per version, and one per contract naming its latest ── */

/**
 * Versions are only ever added, so two of them never collide. The
 * `contract/…` row is what makes two people editing the SAME contract meet:
 * each new version moves it on, from the version its editor read, so the
 * second to save is told rather than quietly superseded.
 */
export const CONTRACTS_SPEC: RegisterSpec<ContractRegister> = {
  key: "contracts",
  topic: "ops.contracts",
  toRows: (reg) => {
    const m = new Map<string, Record<string, unknown>>();
    const latest = new Map<string, ContractVersion>();
    for (const v of reg.versions) {
      m.set(`version/${v.id}`, asRow(v));
      const was = latest.get(v.contractId);
      if (!was || was.at <= v.at) latest.set(v.contractId, v);
    }
    for (const [contractId, v] of latest)
      m.set(`contract/${contractId}`, { contractId, latest: v.id, provider: v.data.provider, title: v.data.title });
    return m;
  },
  fromRows: (rows: Rows) => ({
    versions: rows
      .filter((r) => kindOf(r.id) === "version")
      .map((r) => r.data as unknown as ContractVersion)
      .sort((a, b) => a.at.localeCompare(b.at)),
  }),
  mergeNotes: (docs) => mergeRegisters(docs.filter((d) => Array.isArray(d?.versions))),
  describe: (id, data) =>
    kindOf(id) === "contract"
      ? t("the contract with {provider}", { provider: String((data as { provider?: string } | undefined)?.provider ?? rest(id)) })
      : t("a version of a contract"),
};
